import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSun, FiMoon, FiGlobe } = FiIcons

const Header = () => {
  const { currentLang, toggleLanguage, isDarkMode, toggleTheme, translations } = useApp()

  // Unified button styles and animations - 移除 scale，改用其他效果
  const buttonBaseClasses = "flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 hover:shadow-md"
  const iconButtonClasses = "w-10 h-10 rounded-full flex items-center justify-center border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 hover:shadow-md"

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="pt-4 sm:pt-8 pb-4 sm:pb-6 px-4 md:px-8"
    >
      <div className="max-w-6xl mx-auto">
        {/* Mobile Layout: Stack vertically with centered logo */}
        <div className="flex flex-col space-y-4 sm:hidden">
          {/* Logo Section - Mobile (Centered) */}
          <div className="flex flex-col items-center text-center space-y-3">
            <motion.div 
              className="w-12 h-12 flex-shrink-0"
              whileHover={{ 
                y: -2,
                rotate: 5,
                boxShadow: "0 10px 25px -5px rgba(123, 97, 255, 0.3)",
                transition: { duration: 0.2 }
              }}
              transition={{ duration: 0.2 }}
            >
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="url(#gradient)"/>
                <path d="M8 12h12M10 20h2M14 20h4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <rect x="6" y="8" width="20" height="16" rx="2" stroke="white" strokeWidth="2" fill="none"/>
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#7B61FF'}}/>
                    <stop offset="100%" style={{stopColor:'#F973C1'}}/>
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            <div className="space-y-2">
              <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent leading-tight">
                {translations.appTitle[currentLang]}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 px-4 leading-relaxed">
                {translations.appSlogan[currentLang]}
              </p>
            </div>
          </div>
          
          {/* Controls - Mobile */}
          <div className="flex items-center justify-center space-x-4">
            <motion.button
              whileHover={{ 
                y: -1,
                boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.25)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ y: 0 }}
              onClick={toggleLanguage}
              className={buttonBaseClasses}
              title={currentLang === 'zh' ? 'Switch to English' : '切換到中文'}
            >
              <SafeIcon icon={FiGlobe} className="w-4 h-4" />
              <span className="text-sm font-medium">
                {currentLang === 'zh' ? 'EN' : '中'}
              </span>
            </motion.button>
            
            <motion.button
              whileHover={{ 
                y: -1,
                boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.25)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ y: 0 }}
              onClick={toggleTheme}
              className={iconButtonClasses}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <SafeIcon 
                icon={isDarkMode ? FiSun : FiMoon} 
                className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-gray-600 dark:text-gray-300'}`}
              />
            </motion.button>
          </div>
        </div>

        {/* Desktop Layout: Side by side */}
        <div className="hidden sm:flex justify-between items-center">
          {/* Logo Section - Desktop */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center space-x-4"
          >
            <motion.div 
              className="w-12 h-12 flex-shrink-0"
              whileHover={{ 
                y: -2,
                rotate: 5,
                boxShadow: "0 10px 25px -5px rgba(123, 97, 255, 0.3)",
                transition: { duration: 0.2 }
              }}
              transition={{ duration: 0.2 }}
            >
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="url(#gradient-desktop)"/>
                <path d="M8 12h12M10 20h2M14 20h4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <rect x="6" y="8" width="20" height="16" rx="2" stroke="white" strokeWidth="2" fill="none"/>
                <defs>
                  <linearGradient id="gradient-desktop" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#7B61FF'}}/>
                    <stop offset="100%" style={{stopColor:'#F973C1'}}/>
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            
            <div className="min-w-0 flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent leading-tight">
                {translations.appTitle[currentLang]}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                {translations.appSlogan[currentLang]}
              </p>
            </div>
          </motion.div>

          {/* Controls - Desktop */}
          <div className="flex items-center space-x-4 flex-shrink-0">
            <motion.button
              whileHover={{ 
                y: -1,
                boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.25)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ y: 0 }}
              onClick={toggleLanguage}
              className={buttonBaseClasses}
              title={currentLang === 'zh' ? 'Switch to English' : '切換到中文'}
            >
              <SafeIcon icon={FiGlobe} className="w-4 h-4" />
              <span className="text-sm font-medium">
                {currentLang === 'zh' ? 'English' : '中文'}
              </span>
            </motion.button>
            
            <motion.button
              whileHover={{ 
                y: -1,
                boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.25)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ y: 0 }}
              onClick={toggleTheme}
              className={iconButtonClasses}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <SafeIcon 
                icon={isDarkMode ? FiSun : FiMoon} 
                className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-gray-600 dark:text-gray-300'}`}
              />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.header>
  )
}

export default Header